import { ja } from 'date-fns/locale';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import MicroModal from 'micromodal';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { NumericFormat } from 'react-number-format';
import { Link } from 'react-router-dom';
import { Setting, statusMessages } from '../App';
import { SpinButton } from '../components/SpinButton';
import { Spinner } from '../components/Spinner';
import { AddProducts } from '../components/SubscriptionDetail/AddProducts';
import { ChangeQuantity } from '../components/SubscriptionDetail/ChangeQuantity';
import { useSubscriptionDetail } from '../hooks/use-subscription-detail';
import base from '../styles/Base.module.css';
import style from '../styles/SubscriptionDetail.module.css';

// date-fns v2 Locale を react-datepicker の期待型へ暫定キャスト
registerLocale('ja', {
  ...ja,
  options: ja.options ?? { weekStartsOn: 0, firstWeekContainsDate: 1 },
} as any);

export type StatusMessage = keyof typeof statusMessages;

dayjs.extend(weekday);

export const SubscriptionDetailShokutasu: FC<{ setting: Setting }> = ({ setting }) => {
  const [isLargeWindowSize, setIsLargeWindowSize] = useState<boolean>();
  const [updateCompleteMessage, setUpdateCompleteMessage] = useState('定期便の内容を更新しました');

  // モーダルを閉じた時の共通クリーンアップ
  const cleanupAfterOperation = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    window.parent.postMessage({ action: 'scrollToTop' }, '*');
  }, []);

  // 更新完了モーダルを表示する共通関数
  const showUpdateCompleteModal = useCallback(
    async (message?: string) => {
      setUpdateCompleteMessage(message || '定期便の内容を更新しました');
      await updateSubscriptionRef.current();
      MicroModal.show('modal-update-complete', {
        onClose: cleanupAfterOperation,
      });
    },
    [cleanupAfterOperation],
  );

  // OKボタン用（MicroModalのonCloseで処理するのでcloseだけ呼ぶ）
  const handleUpdateCompleteClose = useCallback(() => {
    MicroModal.close('modal-update-complete');
  }, []);

  // useSubscriptionDetailを1回だけ呼び出し
  const {
    subscription,
    removeItem,
    onClickRemoveAction,
    pageStatus,
    openNewOrderModal,
    onClickQuantityModify,
    onClickQuantityAction,
    onChangeQuantity,
    quantityItem,
    planGroups,
    allowModifyDay,
    updateSubscription,
    updateSubscriptionLineInState,
    removeSubscriptionLineFromState,
  } = useSubscriptionDetail(setting);

  const updateSubscriptionRef = useRef(updateSubscription);
  updateSubscriptionRef.current = updateSubscription;

  useEffect(() => {
    if (!subscription) return;

    const handleMessage = (e: MessageEvent) => {
      if (e.data.windownnerWidth) {
        setIsLargeWindowSize(e.data.windownnerWidth > 999);
      }
    };

    window.addEventListener('message', handleMessage);
    window.parent.postMessage({ action: 'renderSubcription' }, '*');

    // クリーンアップ関数：エフェクト実行前に古いリスナーを削除
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [subscription]);

  if (!subscription) {
    return (
      <>
        <Spinner isLoading={true} />
      </>
    );
  }

  if (!subscription) {
    return <></>;
  }

  const { subscriptionContractId, nextBillingDate, deliveryPriceAmount, deliveryPriceCurrencyCode } = subscription;

  // BOX のサブスクリプションかを判定
  const boxPlanGroup =
    planGroups.length > 0 &&
    planGroups.find((planGroup) => {
      return planGroup.boxes?.some((box) => {
        return subscription!.subscriptionLines.map((p) => p.productId).some((productId) => box.parentProductId === productId);
      });
    });

  // boxPlanGroup = false: 読込中
  const isBoxSubscription = !!boxPlanGroup;

  // TODO
  const deliveryPriceCurrency = deliveryPriceCurrencyCode === 'JPY' ? '￥' : deliveryPriceCurrencyCode;

  let productNewAddAction = <></>;
  if (subscription.shop.showNewAddProduct && !isBoxSubscription && boxPlanGroup !== false) {
    productNewAddAction = (
      <Link to={`/subscriptions/${encodeURIComponent(subscriptionContractId)}/lines/new`}>
        <span className="huckleberry-subscription-detail-btn new-add">他の商品を定期購買に追加する</span>
      </Link>
    );
  }

  let newOrderAction = <></>;
  if (subscription.shop.showNewOrder && subscription.status !== 'PAUSED') {
    newOrderAction = (
      <button className={`${base.sectionBtn} add`} onClick={openNewOrderModal}>
        追加購入する
      </button>
    );
  }

  const discountPriceSubtotal = subscription.subscriptionLines.reduce(
    (total, line) => total + line.currentPriceAmount * line.quantity,
    0,
  );

  // 商品合計（割引後）= sum(lineDiscountedPriceAmount)
  const discountedTotal = subscription.subscriptionLines.reduce((total, line) => total + line.lineDiscountedPriceAmount, 0);

  // 割引額 = 割引前小計 - 割引後合計
  const couponDiscountPrice = discountPriceSubtotal - discountedTotal;

  const totalPrice = discountedTotal;

  return (
    <>
      {/* 注文詳細 */}
      <div className={style.detailMain}>
        <div className={style.detailMainNextDeliveryProductsContent}>
          <h1 className={base.pageHeading}>
            Subscription
            <span className={isLargeWindowSize ? base.large : base.normal}>定期便のお届け商品の追加・変更</span>
          </h1>
          {/* 次回お届け商品 */}
          <div className={`${style.detailMainNextDeliveryProducts} ${style.detailMainBlock}`}>
            {/* style.detailMainNextDeliveryProductsContent */}
            <div className={style.detailMainNextDeliveryProductsContentBlock}>
              <h2 className={`${base.sectionHeading}`}>次回のお届け内容</h2>
              {subscription.subscriptionLines
                .sort((first, line) => {
                  // 親商品を常に上に表示する
                  const isParent =
                    boxPlanGroup &&
                    boxPlanGroup.boxes.some((box) => {
                      return box.parentProductId === line.productId;
                    });
                  return isParent ? 1 : 0; // 親商品以外は０を返して並びを維持する
                })
                .map((line, index) => {
                  const title = line.title;

                  let productQuantityAction = <></>;
                  if (
                    subscription.shop.showChangeQuantity &&
                    planGroups.length > 0 &&
                    !isBoxSubscription &&
                    boxPlanGroup !== false
                  ) {
                    productQuantityAction = (
                      <button
                        className="huckleberry-subscription-detail-btn change-amount"
                        disabled={subscription.disabled}
                        onClick={() => {
                          onClickQuantityModify(line);
                        }}
                      >
                        数量の変更
                      </button>
                    );
                  }

                  let productTitleHtml = <>{title}</>;

                  // variantImageを直接使用（productsInfoは不要になった）
                  const image = line.variantImage;

                  return (
                    <article key={line.lineId} className={base.productItem}>
                      <div className={style.productSummary}>
                        <div className={style.productImageTitleBlock}>
                          <img src={image} alt={line.title} className={style.productImage} />
                          {/* <p>{title}</p> */}
                          <div className={style.productDetail}>
                            <p className={style.productTitle}>{productTitleHtml}</p>
                            {/* 価格 */}
                            <p className={style.productDetailPrice}>
                              <span className={style.productDetailPriceTitle}>定期便価格</span>
                              <NumericFormat
                                displayType="text"
                                value={
                                  // line.lineDiscountedPriceAmount
                                  line.currentPriceAmount
                                }
                                thousandSeparator={true}
                                prefix={deliveryPriceCurrency}
                              />
                            </p>

                            <div className={style.productQuantityBlock}>
                              <ChangeQuantity
                                subscriptionContactId={subscription.subscriptionContractId}
                                line={line}
                                setting={setting}
                                updateSubscription={updateSubscription}
                                updateLine={updateSubscriptionLineInState}
                                removeLine={removeSubscriptionLineFromState}
                                onComplete={showUpdateCompleteModal}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })}

              {/* /お支払い金額 */}

              {/* 金額 */}
              <div className={style.productsPrice}>
                {/* 商品合計・送料 */}
                <div className={style.partialPrice}>
                  {/* 商品合計（税込） */}
                  <div className={`${style.partialPriceProductsPrice} ${style.partialPriceItem}`}>
                    <p>商品小計</p>
                    <p className={style.detailPriceColor}>
                      <NumericFormat
                        displayType="text"
                        value={discountPriceSubtotal}
                        thousandSeparator={true}
                        prefix={deliveryPriceCurrency}
                      />
                    </p>
                  </div>

                  {/* クーポン */}
                  {couponDiscountPrice > 0 && (
                    <div className={`${style.partialPriceShippingPrice} ${style.partialPriceItem}`}>
                      <p>割引額</p>
                      <p>
                        -
                        <NumericFormat
                          displayType="text"
                          value={couponDiscountPrice}
                          thousandSeparator={true}
                          prefix={deliveryPriceCurrency}
                        />
                      </p>
                    </div>
                  )}

                  {/* 適用中クーポン表示 */}
                  {subscription.subscriptionDiscounts
                    .filter((d) => d.coupon)
                    .map((d) => (
                      <div key={d.subscriptionManualDiscountId} className={`${style.appliedCoupon} ${style.partialPriceItem}`}>
                        <p>適用中クーポン</p>
                        <p>{d.title}</p>
                      </div>
                    ))}
                </div>
                {/* /商品合計・送料 */}

                {/* お支払い金額 */}
                <div className={style.totalPrice}>
                  {/* 合計金額 */}
                  <div className={style.totalPriceSummary}>
                    <p>税込合計</p>
                    <p>
                      <NumericFormat
                        className={style.detailPriceColor}
                        displayType="text"
                        value={totalPrice}
                        thousandSeparator={true}
                        prefix={deliveryPriceCurrency}
                      />
                      {' + 送料'}
                    </p>
                  </div>
                </div>
              </div>
              {/* /金額 */}
              <div className={style.shippingNote}>
                <p className={style.shippingCouponNote}>
                  ※クーポンをご利用の場合は、適用後の商品代金を基準に送料を頂戴します。
                </p>
                <p className={style.shippingTableLabel}>＜送料＞</p>
                <table className={style.shippingTable}>
                  <thead>
                    <tr>
                      <th></th>
                      <th>北海道・沖縄を除く地域</th>
                      <th>北海道・沖縄エリア</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>5,000円以上（税込）</td>
                      <td>無料</td>
                      <td>￥620</td>
                    </tr>
                    <tr>
                      <td>5,000円未満（税込）</td>
                      <td>￥880</td>
                      <td>￥1,500</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          {/* /次回お届け商品 */}
        </div>

        {/* 追加商品 */}
        <AddProducts subscription={subscription} setting={setting} planGroups={planGroups} />

        {/* 定期購入一覧に戻る */}
        <div className={`${base.flexJC} ${base.mt20}`}>
          <Link className={`${base.sectionReverseBtn} ${base.cancelUpdateAddress}`} to="/">
            マイページに戻る
          </Link>
        </div>
        {/* /定期購入一覧に戻る */}

        <div className="micromodal" id="modal-remove-product" aria-hidden="true">
          <div className={`${base.micromodalOverlay} micromodal-overlay`} tabIndex={-1}>
            <div
              role="dialog"
              className={base.micromodalOverlayDialog}
              aria-modal="true"
              aria-labelledby="modal-remove-product-title"
            >
              <header>
                <button aria-label="Close modal" className={base.closeModal} data-micromodal-close></button>
                <h2 id="modal-remove-product-title">商品の削除</h2>
              </header>
              <main id="modal-remove-product-content">
                <p>「{removeItem ? removeItem.title : ''}」を削除します。よろしいですか？</p>
              </main>
              <footer>
                <button className={base.sectionBtn} onClick={onClickRemoveAction} disabled={pageStatus.removeAction}>
                  削除
                </button>
                <button className="btn clear" data-micromodal-close>
                  戻る
                </button>
              </footer>
            </div>
          </div>
        </div>

        <div className="micromodal" id="modal-remove-product-complete" aria-hidden="true">
          <div className={`${base.micromodalOverlay} micromodal-overlay`} tabIndex={-1}>
            <div
              role="dialog"
              className={base.micromodalOverlayDialog}
              aria-modal="true"
              aria-labelledby="modal-remove-product-complete-title"
            >
              <header>
                <button aria-label="Close modal" className={base.closeModal} data-micromodal-close></button>
                <h2 id="modal-remove-product-complete-title">商品の削除</h2>
              </header>
              <main id="modal-remove-product-complete-content">
                <p>「{removeItem ? removeItem.title : ''}」を削除しました。</p>
              </main>
              <footer>
                <button className={base.sectionBtn} data-micromodal-close>
                  ＯＫ
                </button>
              </footer>
            </div>
          </div>
        </div>

        <div className="micromodal" id="modal-quantity-modify" aria-hidden="true">
          <div className={`${base.micromodalOverlay} micromodal-overlay`} tabIndex={-1}>
            <div
              role="dialog"
              className={base.micromodalOverlayDialog}
              aria-modal="true"
              aria-labelledby="modal-quantity-modify-title"
            >
              <header>
                <button aria-label="Close modal" className={base.closeModal} data-micromodal-close></button>
                <h2 id="modal-quantity-modify-title">数量の変更</h2>
              </header>
              <main id="modal-quantity-modify-content">
                <p>
                  数量を選択してください。
                  <br />
                  変更は次回のご注文から反映されます。
                </p>
                <SpinButton min={1} value={quantityItem ? quantityItem.quantity : 1} onChange={onChangeQuantity} />
              </main>
              <footer>
                <button className={base.sectionBtn} disabled={pageStatus.quantityAction} onClick={onClickQuantityAction}>
                  保存
                </button>
              </footer>
            </div>
          </div>
        </div>
        <div className="micromodal" id="modal-quantity-modify-complete" aria-hidden="true">
          <div className={`${base.micromodalOverlay} micromodal-overlay`} tabIndex={-1}>
            <div
              role="dialog"
              className={base.micromodalOverlayDialog}
              aria-modal="true"
              aria-labelledby="modal-quantity-modify-complete-title"
            >
              <header>
                <button aria-label="Close modal" className={base.closeModal} data-micromodal-close></button>
                <h2 id="modal-quantity-modify-complete-title">数量の変更</h2>
              </header>
              <main id="modal-quantity-modify-complete-content">
                <p>
                  数量の変更を受け付けました。
                  <br />
                  次回のご注文から反映されます。
                </p>
              </main>
              <footer>
                <button className={base.sectionBtn} data-micromodal-close>
                  ＯＫ
                </button>
              </footer>
            </div>
          </div>
        </div>
        <div className="micromodal" id="modal-error" aria-hidden="true">
          <div className={`${base.micromodalOverlay} micromodal-overlay`} tabIndex={-1}>
            <div role="dialog" className={base.micromodalOverlayDialog} aria-modal="true" aria-labelledby="modal-error-title">
              <header>
                <button aria-label="Close modal" className={base.closeModal} data-micromodal-close></button>
                <h2 id="modal-error-title">エラー</h2>
              </header>
              <main id="modal-error-content">
                <p>エラーが発生しました。店舗へお問い合わせください。</p>
              </main>
              <footer>
                <button className={base.sectionBtn} data-micromodal-close>
                  ＯＫ
                </button>
              </footer>
            </div>
          </div>
        </div>
      </div>
      {/* /注文詳細 */}

      {/* 更新完了モーダル（数量変更・削除・追加 共通） */}
      <div className="micromodal" id="modal-update-complete" aria-hidden="true">
        <div className={`${base.micromodalOverlay} micromodal-overlay`} tabIndex={-1} data-micromodal-close>
          <div
            role="dialog"
            className={`${base.micromodalOverlayDialog} ${style.OtherMenuModal}`}
            aria-modal="true"
            aria-labelledby="modal-update-complete-title"
          >
            <header className={base.ModalHeader}>
              <button aria-label="Close modal" className={base.closeModal} onClick={handleUpdateCompleteClose}></button>
              <h2 id="modal-update-complete-title" className={base.ModalTitle}>
                {updateCompleteMessage}
              </h2>
            </header>
            <main className={base.ModalHeader}>
              <div className={`${base.ModalFooter}`} style={{ textAlign: 'center' }}>
                <button className={base.sectionBtn} onClick={handleUpdateCompleteClose}>
                  ＯＫ
                </button>
              </div>
            </main>
          </div>
        </div>
      </div>

      <div className="micromodal" id="modal-allow-modify-day" aria-hidden="true">
        <div className={`${base.micromodalOverlay} micromodal-overlay`} tabIndex={-1} data-micromodal-close>
          <div
            role="dialog"
            className={base.micromodalOverlayDialog}
            aria-modal="true"
            aria-labelledby="modal-allow-modify-day-title"
          >
            <header>
              <button aria-label="Close modal" className={base.closeModal} data-micromodal-close></button>
              <h2 id="modal-allow-modify-day-title">{allowModifyDay.title}</h2>
            </header>
            <main id="modal-allow-modify-day-content">
              <p>
                {allowModifyDay.title}は次回ご注文日の{allowModifyDay.day}日前以降はご利用できません。
                <br />
                次回のご注文日、【{dayjs(nextBillingDate).format('YYYY/MM/DD')}】以降にご利用が可能となります。
              </p>
            </main>
            <footer>
              <button className={base.sectionBtn} data-micromodal-close>
                ＯＫ
              </button>
            </footer>
          </div>
        </div>
      </div>
      <div className="micromodal" id="modal-stock-error" aria-hidden="true">
        <div className={`${base.micromodalOverlay} micromodal-overlay`} tabIndex={-1}>
          <div
            role="dialog"
            className={base.micromodalOverlayDialog}
            aria-modal="true"
            aria-labelledby="modal-stock-error-title"
          >
            <header>
              <button aria-label="Close modal" className={base.closeModal} data-micromodal-close></button>
              <h2 id="modal-stock-error-title">商品の数量変更</h2>
            </header>
            <main id="modal-stock-error-content">
              <p>
                在庫がありません。
                <br />
                入荷をお待ちいただくか、他の商品に変更をお願いします。
              </p>
            </main>
            <footer>
              <button className={base.sectionBtn} data-micromodal-close>
                ＯＫ
              </button>
            </footer>
          </div>
        </div>
      </div>
    </>
  );
};
